// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import MuiSelect, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import { Checkbox, Fade, ListItemText, MenuItem, Tooltip } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react';
import cx from 'classnames';

import Flex from '@shared/components/Flex';
import Loading from '@shared/components/Loading';
import Ellipsis from '@shared/components/Ellipsis';
import { getTestAttr } from '@shared/utils/common';
import { getScrollAttr } from '@shared/utils/form';
import { Id } from '@shared/types/common';

export interface Option {
  id: Id;
  label: string;
}

type Classes = Pick<MuiSelectProps, 'classes'>;

export type Value = Array<Option['id']> | Option['id'];

export enum SerializerType {
  string = 'string',
  number = 'number'
}

export enum Size {
  medium = 'medium',
  small = 'small'
}

export interface SelectProps extends Classes, Omit<MuiSelectProps, 'classes' | 'onChange'> {
  className?: string;
  classes?: Record<string, string>;
  options: Array<Option>;
  placeholder: string;
  withTooltipOnOption?: boolean;
  serializerType?: SerializerType;
  loading?: boolean;
  field?: Field;
  value?: Value;
  errorText?: React.ReactNode;
  size?: Size;
  onChange?: (value: Value, e?, option?: Option | Array<Option>, oldValue?: Value) => any;
  testAttributeArgs: {
    module: string;
    elementDescription: string;
  };
}

const defaultSize = Size.medium;

@observer
class Select extends React.Component<SelectProps> {
  static defaultProps = {
    options: [],
    serializerType: SerializerType.string,
    withTooltipOnOption: true,
  };

  constructor(props: SelectProps) {
    super(props);

    const { field } = props;

    this.setFieldOption(field?.value);
  }

  private setFieldOption = (value) => {
    if (value) {
      const { field } = this.props;

      const option = Array.isArray(value)
        ? value.map(this.getOption)
        : this.getOption(value);

      if (option) {
        field.set('extra', option);
      }
    }
  }

  private handleChange = (e) => {
    // e.persist();

    const { value } = e.target;
    const { field, onChange } = this.props;
    const oldValue = this.props.value;

    const serializedValue = this.serialize(value);

    if (field) {
      // https://github.com/foxhound87/mobx-react-form/issues/251
      field.container().update({ [field.name]: serializedValue });
      field.validate();

      this.setFieldOption(serializedValue);
    }

    if (onChange) {
      const option = Array.isArray(value)
        ? value.map((id) => this.getOption(id))
        : this.getOption(value);

      onChange(serializedValue, e, option, oldValue);
    }
  }

  // Find item with matching 'id' from the options array.
  private getOption = (id: Option['id']) => {
    return this.props.options.find((option) => option.id == id) as Option;
  }

  private renderValue = (value: Value) => {
    const { renderValue } = this.props;

    if (renderValue) {
      return renderValue(value);
    }

    const label = Array.isArray(value)
      ? value.map((id) => this.getOption(id)?.label).join(', ')
      : this.getOption(value)?.label;

    const { placeholder, loading } = this.props;
    const classes = withStyles.getClasses(this.props);

    if (loading) {
      return (
        <Flex justifyContent="space-between" alignItems="center" autoWidth={false} wrap="nowrap">
          <Ellipsis
            withTooltip={false}
            maxWidth="75%"
            text={label || placeholder}
            classes={{ root: label ? classes.valueLoading : classes.placeholder }}
          />
          <Loading progressProps={{ size: 15 }} />
        </Flex>
      );
    }

    if (!value || value === '' || Array.isArray(value) && value.length === 0) {
      return (
        <Ellipsis
          withTooltip={false}
          maxWidth="100%"
          text={placeholder}
          classes={{ root: classes.placeholder }}
        />
      );
    }

    const tooltipTitle = label || '';

    if (Array.isArray(value)) {
      return (
        <Tooltip title={tooltipTitle} placement="top-start">
          <div className={cx(classes.value, classes.valueMultiple)}>
            {label} <span className={classes.valueCount}>{value.length}</span>
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={tooltipTitle} placement="top-start">
        <div className={classes.value}>
          {label}
        </div>
      </Tooltip>
    );
  }

  private serialize = (value: Value) => {
    const serializers = {
      [SerializerType.string]: String,
      [SerializerType.number]: Number,
    };
    const { serializerType = SerializerType.string } = this.props;

    const serialize = serializers[serializerType];

    if (Array.isArray(value)) {
      return value.map((val) => serialize(val));
    }

    return serialize(value);
  }

  render() {
    const {
      loading,
      options,
      multiple,
      fullWidth,
      MenuProps,
      field,
      error,
      errorText,
      value,
      testAttributeArgs,
      name,
      disabled,
      size = defaultSize,
      withTooltipOnOption
    } = this.props;
    const classes = withStyles.getClasses(this.props);
    const selectValue = field && field.value ? field.value : value;
    const serializedValue = (selectValue && options.length !== 0) ? this.serialize(selectValue) : '';

    const hasError = Boolean(error || field?.error);
    const sizeClasses = {
      [Size.medium]: classes.rootBig,
      [Size.small]: classes.rootSmall
    };

    return (
      <div
        className={cx(classes.root, sizeClasses[size], { [classes.rootFullWidth]: fullWidth })}
        {...getScrollAttr(name || field?.name)}
        {...getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'select-field')}
      >
        <MuiSelect
          displayEmpty
          classes={{
            root: classes.inputInner,
            disabled: classes.rootDisabled,
          }}
          name={name || field?.name}
          error={hasError}
          className={classes.input}
          disabled={disabled || loading}
          fullWidth={fullWidth}
          variant="outlined"
          multiple={multiple}
          value={multiple && !Array.isArray(serializedValue) ? [] : serializedValue}
          MenuProps={{
            // getContentAnchorEl: null,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            classes: {
              list: classes.list,
            },
            PopoverClasses: {
              paper: classes.menu
            },
            TransitionComponent: Fade,
            ...MenuProps,
          }}
          inputProps={getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'select-value')}
          renderValue={this.renderValue}
          onChange={this.handleChange}
        >
          {options.map((option) => {
            let selected = false;
            const serializedId = this.serialize(option.id) as Option['id'];

            selected = Array.isArray(serializedValue)
              ? serializedValue.includes(serializedId)
              : option.id === serializedValue;

            return (
              <MenuItem
                disableRipple
                key={serializedId}
                {...getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'select-option')}
                value={serializedId}
                classes={{
                  root: classes.menuItem,
                  selected: classes.menuItemSelected
                }}
              >
                {multiple && <Checkbox classes={{ root: classes.checkbox }} checked={selected} />}
                <Tooltip title={withTooltipOnOption ? option.label : ''} placement="top-start">
                  <div className={cx(classes.optionItemWrapper, { [classes.optionItemWrapperMultiple]: multiple })}>
                    <ListItemText primary={option.label} classes={{ primary: classes.menuItemText }} />
                  </div>
                </Tooltip>
                {selected && !multiple && <DoneIcon className={classes.selectedIcon} />}
              </MenuItem>
            );
          })}
        </MuiSelect>
        {hasError && <Ellipsis classes={{ root: classes.errorText }} text={field?.error || errorText} />}
      </div>
    );
  }
}

const SelectStyled = withStyles(
  Select, 
  (theme, props) => ({
    "root": {
      minWidth: 150,
      position: 'relative' as const
    },
    "rootBig": {
      height: 48,
  
      '& $value': {
        fontSize: 16
      }
    },
    "rootSmall": {
      height: 32,
  
      '& $value': {
        fontSize: 12
      }
    },
    "rootDisabled": {
      '& $value, $valueLoading': {
        opacity: .6
      }
    },
    "rootFullWidth": {
      width: '100%',
    },
    "input": {
      width: '100%',
      height: '100%',
    },
    "inputInner": {
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    "placeholder": {
      opacity: .4,
    },
    "value": {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: 1.2,
      width: '100%',
    },
    "valueLoading": {},
    "valueMultiple": {
      width: 'calc(100% - 20px)',
    },
    "valueCount": {
      right: 32,
      width: 18,
      height: 18,
      top: 16,
      position: 'absolute' as const,
      fontSize: 10,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50% 50%',
      color: 'white',
      backgroundColor: theme.palette.colors.primaryAccent,
    },
    "menu": {
      marginTop: theme.spacing(1),
      border: `1px solid ${theme.palette.colors.black}`
    },
    "list": {
      maxHeight: 200,
    },
    "menuItem": {
      fontSize: 15,
      color: theme.palette.colors.greyDark,
      transition: `${theme.transitions.duration.standard}s`,
      position: 'relative' as const,
  
      '&:hover': {
        color: theme.palette.colors.black,
        backgroundColor: theme.palette.colors.greyLight5
      },
    },
    "menuItemSelected": {
      '& $menuItemText': {
        color: theme.palette.colors.black,
        fontWeight: 500,
      },
  
      '& $optionItemWrapper': {
        width: '88%'
      }
    },
    "selectedIcon": {
      position: 'absolute' as const,
      right: 0,
      color: theme.palette.colors.black,
    },
    "checkbox": {
      padding: 0,
      marginRight: theme.spacing(2),
  
      '&:hover': {
        backgroundColor: 'transparent'
      }
    },
    "menuItemText": {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    "optionItemWrapper": {
      width: '100%',
    },
    "optionItemWrapperMultiple": {
      width: 'calc(100% - 32px)',
    },
    "errorText": {
      ...theme.shortcuts.errorText
    }
  })
);

export default SelectStyled;